import { Fragment, memo, ReactElement, useMemo } from 'react';
import {
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  Tooltip,
  useEventCallback,
} from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import { useFormikContext } from 'formik';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import { ResearchPerson } from 'civil-status/view-models/research-category-form';
import { FormControl } from '@mui/material';
import ForeignStateList, { ForeignStateVM } from 'shell/components/shared/foreign-state-list';
import MunicipalityList, { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import ListGender from 'shell/components/shared/gender';

interface FormFilter {
  isChoiceList?: boolean;
}

const FormFilterComponent = ({ isChoiceList = false }: FormFilter): ReactElement => {
  const formContext = useFormikContext<ResearchPerson>();

  const handleForeignStateListChange = useEventCallback(
    (state: ForeignStateVM | null | undefined) => {
      formContext.setFieldValue('state', state);
    }
  );

  const handleMunicipalityChange = useEventCallback((municipality: MunicipalityAnprVM | null) => {
    formContext.setFieldValue('municipality', municipality);
  });

  const onChangeGender = useEventCallback((event: SelectChangeEvent<string>) => {
    formContext.setFieldValue('gender', event.target.value);
  });

  const disabledSearch: boolean = useMemo(() => {
    // possibile una sola tipologia di ricerca per volta
    const is1Search = Boolean(formContext.values.fiscalCode);
    const is2Search = Boolean(
      formContext.values.surname || formContext.values.name || formContext.values.birthDate
    );
    const is3Search = Boolean(formContext.values.idAnpr);
    const is4Search = Boolean(formContext.values.idAnsc);
    // const is5Search = Boolean(
    //   formContext.values.state || formContext.values.municipality
    // );

    if (
      is1Search &&
      (is2Search || is3Search || is4Search)
      // || is5Search
    ) {
      // controllo tipologia 1
      return true;
    }

    if (
      is2Search &&
      (is1Search || is3Search || is4Search) // || is5Search
    ) {
      // controllo tipologia 2
      return true;
    }

    if (
      is3Search &&
      (is1Search || is2Search || is4Search) // || is5Search
    ) {
      // controllo tipologia 3
      return true;
    }

    if (
      is4Search &&
      (is1Search || is2Search || is3Search) // || is5Search
    ) {
      // controllo tipologia 4
      return true;
    }

    // if (is5Search && (is1Search || is2Search || is3Search || is4Search)) {
    //   // controllo tipologia 5
    //   return true;
    // }

    return false;
  }, [formContext.values]);

  return (
    <Grid container spacing={2}>
      {isChoiceList ? (
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <RadioGroup
              row
              aria-label="Scelta lista"
              id="isEvent"
              value={formContext.values?.isEvent || 'false'}
              onChange={() => {
                formContext.setFieldValue('isEvent', !formContext.values?.isEvent);
              }}
            >
              <FormControlLabel value="false" control={<Radio />} label="Lista intestatari" />
              <FormControlLabel value="true" control={<Radio />} label="Lista eventi" />
            </RadioGroup>
          </FormControl>
        </Grid>
      ) : undefined}
      {/* Tipo di ricerca 1: */}
      <Grid item xs={12} md={6}>
        <PmiTextField
          label="Codice fiscale"
          aria-label="Codice fiscale"
          name="fiscalCode"
          id="fiscalCode"
          fullWidth
          value={formContext.values.fiscalCode?.toUpperCase() || ''}
          onChange={(event) => formContext.setFieldValue('fiscalCode', event.target.value)}
          inputProps={{
            maxLength: '16',
          }}
          error={formContext.touched?.fiscalCode && Boolean(formContext.errors?.fiscalCode)}
          helperText={formContext.errors?.fiscalCode}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      {/* Tipo di ricerca 2: */}
      <Grid item xs={12} md={9}>
        <PmiTextField
          label="Cognome"
          aria-label="Cognome"
          id="surname"
          fullWidth
          value={formContext.values.surname || ''}
          onChange={(event) => formContext.setFieldValue('surname', event.target.value)}
          error={formContext.touched?.surname && Boolean(formContext.errors?.surname)}
          helperText={formContext.errors?.surname}
        />
      </Grid>
      <Grid item xs={12} md={9}>
        <PmiTextField
          label="Nome"
          aria-label="Nome"
          id="name"
          fullWidth
          value={formContext.values.name || ''}
          onChange={(event) => formContext.setFieldValue('name', event.target.value)}
          error={formContext.touched?.name && Boolean(formContext.errors?.name)}
          helperText={formContext.errors?.name}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <ListGender
          value={formContext.values?.gender || ''}
          onChange={onChangeGender}
          error={formContext.touched?.gender && Boolean(formContext.errors?.gender)}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <DatePicker
          label="Data nascita"
          aria-label="Data nascita"
          value={formContext.values?.birthDate || null}
          onChange={(date) => {
            formContext.setFieldValue('birthDate', date);
          }}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="birthDate"
              fullWidth
              error={formContext.touched?.birthDate && Boolean(formContext.errors?.birthDate)}
              helperText={formContext.errors?.birthDate}
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Nascita"
            id="isForeign"
            defaultValue={false}
            value={formContext.values?.isForeign}
            onChange={() => {
              formContext.setValues({
                ...formContext.values,
                isForeign: !formContext.values.isForeign,
                state: undefined,
                municipality: undefined,
              });
            }}
          >
            <FormControlLabel value="false" control={<Radio />} label="Nascita Italia" />
            <FormControlLabel value="true" control={<Radio />} label="Nascita Estero" />
          </RadioGroup>
        </FormControl>
      </Grid>
      {formContext.values?.isForeign ? (
        <Grid item xs={12} md={4}>
          <ForeignStateList
            value={formContext.values?.state || null}
            onChange={handleForeignStateListChange}
            label="Stato nascita"
            id="foreignState"
            error={formContext.touched?.state && Boolean(formContext.errors?.state)}
          />
        </Grid>
      ) : (
        <Fragment>
          <Grid item xs={12} md={4}>
            <MunicipalityList
              value={formContext.values?.municipality || null}
              onChange={handleMunicipalityChange}
              label="Comune di nascita"
              error={formContext.touched?.municipality && Boolean(formContext.errors?.municipality)}
              fromDate={formContext.values?.birthDate}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <PmiTextField
              aria-label="Provincia"
              label="Provincia di nascita"
              id="county"
              name="county"
              fullWidth
              disabled
              type="text"
              variant="outlined"
              value={formContext.values?.municipality?.province || ''}
            />
          </Grid>
        </Fragment>
      )}
      <Grid item xs={12}>
        <Divider />
      </Grid>
      {/* Tipo di ricerca 3: */}
      <Grid item xs={12} md={3}>
        <PmiTextField
          label="ID Anpr"
          aria-label="ID Anpr"
          id="ID_Anpr"
          type="number"
          fullWidth
          value={formContext.values.idAnpr || ''}
          onChange={(event) => formContext.setFieldValue('idAnpr', event.target.value)}
          error={formContext.touched?.idAnpr && Boolean(formContext.errors?.idAnpr)}
          helperText={formContext.errors?.idAnpr}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      {/* Tipo di ricerca 4: */}
      <Grid item xs={12} md={3}>
        <PmiTextField
          label="ID Ansc Soggetto"
          aria-label="ID Ansc Soggetto"
          id="ID_Ansc"
          type="number"
          fullWidth
          value={formContext.values.idAnsc || ''}
          onChange={(event) => formContext.setFieldValue('idAnsc', event.target.value)}
          error={formContext.touched?.idAnsc && Boolean(formContext.errors?.idAnsc)}
          helperText={formContext.errors?.idAnsc}
        />
      </Grid>
      <Grid item container spacing={2} xs={12} justifyContent="space-between" display="flex">
        <Grid item container spacing={2} xs={12} justifyContent="flex-end" display="flex">
          <Grid item xs={12} md={2}>
            <PmiButton
              variant="contained"
              color="secondary"
              fullWidth
              onClick={() => formContext.resetForm()}
            >
              Pulisci
            </PmiButton>
          </Grid>
          <Grid item xs={12} md={2}>
            <Tooltip
              arrow
              title={disabledSearch ? 'È possibile la ricerca per una sola tipologia' : ''}
            >
              <span style={{ height: 'fit-content' }}>
                <PmiButton
                  variant="contained"
                  color="secondary"
                  fullWidth
                  type="submit"
                  disabled={disabledSearch}
                >
                  Cerca
                </PmiButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

FormFilterComponent.displayName = 'Filter person - Form Filter component';

const FormFilter = memo(FormFilterComponent);

export default FormFilter;
